import React, { useCallback, useEffect, useRef, useState } from "react";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import ReCAPTCHA from "react-google-recaptcha";

import { AppConfig } from "@config/config";
import { FetchStatus } from "@src/types/api/FetchStatus.types";
import { sendContactMessage } from "@services/tixApi/methods";

import Input from "@components/toolkit/Input/Input";
import Textarea from "@components/toolkit/Textarea/Textarea";
import SendButton from "@components/toolkit/SendButton/SendButton";
import RecaptchaDisclaimer from "@components/common/RecaptchaDisclaimer/RecaptchaDisclaimer";

import SendingStatusBadge from "../SendingStatusBadge/SendingStatusBadge";
import { contactFormSchema } from "./contactForm.schema";
import "./ContactForm.scss";
import { CAPTCHA_ENABLED } from "@config/helpers";

const RESET_FORM_TIMEOUT = 5000; // [ms]

interface ContactForm {
  email: string;
  message: string;
}

const ContactForm = () => {
  const [sendStatus, setSendStatus] = useState<FetchStatus>("idle");
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const { t } = useTranslation(["landingPage", "formErrors", "common"]);

  const validationSchema = contactFormSchema(t);

  useEffect(() => {
    if (sendStatus !== "success") return;

    setTimeout(() => {
      setSendStatus("idle");
    }, RESET_FORM_TIMEOUT);
  }, [sendStatus]);

  const handleSendContactMessage = useCallback(
    async ({ email, message }: ContactForm) => {
      if (sendStatus === "loading") return;

      let recaptchaToken: string | undefined;

      if (CAPTCHA_ENABLED) {
        try {
          const token = await recaptchaRef.current?.executeAsync();
          recaptchaToken = token ?? undefined;
        } catch (err) {
          setSendStatus("failed");
          return;
        }

        if (!recaptchaToken) {
          setSendStatus("failed");
          return;
        }
      }

      setSendStatus("loading");

      sendContactMessage(email, message, recaptchaToken)
        .then((res) => {
          if (res?.status === 204) {
            setSendStatus("success");
          } else {
            setSendStatus("failed");
          }
        })
        .catch(() => {
          setSendStatus("failed");
        })
        .finally(() => {
          recaptchaRef.current?.reset();
        });
    },
    [sendStatus]
  );

  const { values, touched, errors, handleChange, handleBlur, handleSubmit, resetForm } = useFormik<ContactForm>({
    initialValues: { email: "", message: "" },
    validationSchema,
    onSubmit: handleSendContactMessage
  });

  useEffect(() => {
    if (sendStatus !== "success") return;

    const timeout = setTimeout(() => {
      resetForm();
      setSendStatus("idle");
    }, RESET_FORM_TIMEOUT);

    return () => clearTimeout(timeout);
  }, [sendStatus, resetForm]);

  const submitting = sendStatus === "loading";

  return (
    <div className="contact-form">
      <Input
        name="email"
        label={t("landingPage:footer.contactForm.inputs.email.label")}
        placeholder={t("landingPage:footer.contactForm.inputs.email.placeholder")}
        value={values.email}
        onChange={handleChange}
        error={errors.email && touched.email ? errors.email : ""}
        onBlur={handleBlur}
      />
      <Textarea
        name="message"
        label={t("landingPage:footer.contactForm.inputs.message.label")}
        placeholder={t("landingPage:footer.contactForm.inputs.message.placeholder")}
        value={values.message}
        onChange={handleChange}
        onBlur={handleBlur}
        error={errors.message && touched.message ? errors.message : ""}
        className="mt-5"
      />

      <div className="form-buttons mt-7">
        <SendButton onClick={handleSubmit} loading={submitting}>
          {submitting ? t("common:sending") : t("common:sendMessage")}
        </SendButton>

        <SendingStatusBadge status={sendStatus} />
      </div>

      {CAPTCHA_ENABLED && (
        <>
          <RecaptchaDisclaimer className="mt-6" />

          <ReCAPTCHA
            ref={recaptchaRef}
            style={{ visibility: "hidden" }}
            size="invisible"
            sitekey={AppConfig.CaptchaPublicKey!}
          />
        </>
      )}
    </div>
  );
};

export default ContactForm;
